import {
  IconLetterT,
  IconAlignLeft,
  IconHash,
  IconMail,
  IconPhone,
  IconList,
  IconListDetails,
  IconCalendar,
  IconClock,
  IconCircleDot,
  IconCheckbox,
  IconPhoto,
  IconVideo,
  IconPencil,
  IconCalendarTime,
} from "@tabler/icons-react";
import type { ActionType, FieldType, QuestionType } from "../types/form.types";

export const fieldTypes: FieldType[] = [
  { id: "single-line-text", label: "Single Line Text", icon: IconLetterT, category: "Basic Input" },
  { id: "multi-line-text", label: "Multi-line Text", icon: IconAlignLeft, category: "Basic Input" },
  { id: "number", label: "Number", icon: IconHash, category: "Basic Input" },
  { id: "phone", label: "Phone Number", icon: IconPhone, category: "Basic Input" },
  { id: "email", label: "Email", icon: IconMail, category: "Basic Input" },
  { id: "date", label: "Date", icon: IconCalendar, category: "Basic Input" },
  { id: "time", label: "Time", icon: IconClock, category: "Basic Input" },
  { id: "date-time", label: "Date & Time", icon: IconCalendarTime, category: "Basic Input" },
  { id: "radio", label: "Single Choice", icon: IconCircleDot, category: "Choice" },
  { id: "checkbox", label: "Multiple Choice", icon: IconCheckbox, category: "Choice" },
  { id: "dropdown", label: "Dropdown", icon: IconList, category: "Choice" },
  { id: "acknowledgement", label: "Acknowledgement", icon: IconCheckbox, category: "Choice" },
  { id: "photo-upload", label: "Photo Upload", icon: IconPhoto, category: "Advanced" },
  { id: "video-upload", label: "Video Upload", icon: IconVideo, category: "Advanced" },
  { id: "signature", label: "Signature", icon: IconPencil, category: "Advanced" },
  { id: "repeating-group", label: "Repeating Group", icon: IconListDetails, category: "Advanced" },
];

export const FIELD_CATEGORIES = ["Basic Input", "Choice", "Advanced"];

export const GROUP_CHILD_EXCLUDED_TYPES: QuestionType[] = [
  "photo-upload",
  "video-upload",
  "signature",
  "repeating-group",
];

export const GROUP_ALLOWED_ACTIONS: ActionType[] = ["show", "hide", "require"];

// Field types offered when adding a new question into the given scope.
// Inside a repeating group (scope.groupId set), types an entry cannot itself hold are hidden.
export const getAddableFieldTypes = (scope?: { groupId?: string }): FieldType[] =>
  scope?.groupId
    ? fieldTypes.filter((f) => !GROUP_CHILD_EXCLUDED_TYPES.includes(f.id))
    : fieldTypes;

// Field types offered by an existing question's "change type" switcher.
// Switching type never turns a question into (or out of) a "repeating-group" — a group's
// fields/groupConfig are only set up by addQuestion — and a group child can't
// switch into a type an entry isn't allowed to hold.
export const getSwitchableFieldTypes = (scope?: { groupId?: string }): FieldType[] =>
  fieldTypes.filter((f) => {
    if (f.id === "repeating-group") return false;
    if (scope?.groupId && GROUP_CHILD_EXCLUDED_TYPES.includes(f.id)) return false;
    return true;
  });

export const getFieldType = (id: string) => fieldTypes.find((f) => f.id === id);
export const getFieldIcon = (id: string) => getFieldType(id)?.icon || IconLetterT;

// Condition options by field type
export const TEXT_CONDITIONS = [
  { value: "is", label: "Is" },
  { value: "is_not", label: "Is not" },
  { value: "contains", label: "Contains" },
  { value: "does_not_contain", label: "Does not contain" },
  { value: "starts_with", label: "Starts with" },
  { value: "does_not_start_with", label: "Does not start with" },
  { value: "ends_with", label: "Ends with" },
  { value: "does_not_end_with", label: "Does not end with" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
];

export const CHOICE_CONDITIONS = [
  { value: "is", label: "Is" },
  { value: "is_not", label: "Is not" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
  { value: "is_any_of", label: "Is any of" },
  { value: "is_not_any_of", label: "Is not any of" },
];

export const OTHER_CONDITIONS = [
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
];

export const DATE_CONDITIONS = [
  { value: "is", label: "Is" },
  { value: "is_not", label: "Is not" },
  { value: "is_before", label: "Is before" },
  { value: "is_after", label: "Is after" },
  { value: "is_on_or_before", label: "Is on or before" },
  { value: "is_on_or_after", label: "Is on or after" },
  { value: "is_between", label: "Is between" },
  { value: "is_empty", label: "Is empty" },
  { value: "is_not_empty", label: "Is not empty" },
];

export const ACTION_OPTIONS = [
  { value: "show", label: "Show questions" },
  { value: "hide", label: "Hide questions" },
  { value: "require", label: "Require answer" },
  { value: "show_section", label: "Show section" },
  { value: "hide_section", label: "Hide section" },
  { value: "show_page", label: "Show page" },
  { value: "hide_page", label: "Hide page" },
  { value: "jump_to_page", label: "Jump to page" },
];

export const getConditionsForType = (type: string) => {
  if (["radio", "checkbox", "dropdown"].includes(type)) return CHOICE_CONDITIONS;
  if (["single-line-text", "multi-line-text", "email", "phone", "number"].includes(type))
    return TEXT_CONDITIONS;
  if (["date", "time", "date-time"].includes(type)) return DATE_CONDITIONS;
  return OTHER_CONDITIONS;
};

export const UNIT_OPTIONS = [
  { value: "", label: "None" },
  { value: "second", label: "second" },
  { value: "minute", label: "minute" },
  { value: "hour", label: "hour" },
  { value: "°F", label: "°F" },
  { value: "°C", label: "°C" },
  { value: "amps", label: "amps" },
  { value: "volts", label: "volts" },
  { value: "watts", label: "watts" },
  { value: "kg", label: "kg" },
  { value: "lbs", label: "lbs" },
  { value: "m", label: "m" },
  { value: "ft", label: "ft" },
  { value: "%", label: "%" },
  { value: "$", label: "$ (USD)" },
];

export const UNIT_DROPDOWN_OPTIONS = [{ value: "none", label: "None" }, ...UNIT_OPTIONS.slice(1)];
